import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import * as yup from "yup";
import { toast } from "sonner";

import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfRadio from "@/components/rhfFields/RhfRadio";
import { Button } from "@/components/ui/button";

const apiBase =
  import.meta.env.VITE_API_URL || "https://dev.api.anthro.care/api";

type Status = "loading" | "success" | "error" | "invalid";

/** Backend may return `{ success, message, stack? }` with 4xx or sometimes 200 + success:false */
function readPublicApiMessage(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const m = (body as { message?: unknown }).message;
  return typeof m === "string" && m.trim() ? m.trim() : undefined;
}

function isPublicApiSuccess(httpOk: boolean, body: unknown): boolean {
  if (!httpOk) return false;
  if (body && typeof body === "object" && "success" in body) {
    const s = (body as { success: unknown }).success;
    if (s === false) return false;
  }
  return true;
}

const UNSUBSCRIBE_REASONS = [
  { label: "Emails are sent too frequently.", value: "too_frequent" },
  { label: "I never signed up for this mailing list.", value: "never_signed_up" },
  { label: "The emails are inappropriate.", value: "inappropriate" },
  { label: "The emails are spam and should be reported.", value: "spam" },
  { label: "Other (fill in reason below).", value: "other" },
];

const unsubscribeFeedbackSchema = yup.object({
  reason: yup.string().required("Please select a reason"),
  otherDetails: yup.string().when("reason", {
    is: "other",
    then: (schema) =>
      schema
        .trim()
        .required("Please fill in your reason")
        .min(3, "Please add a bit more detail"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

function resolveFeedbackReasonText(
  reasonValue: string,
  otherDetails: string,
): string {
  if (reasonValue === "other") {
    return otherDetails.trim();
  }
  const opt = UNSUBSCRIBE_REASONS.find((o) => o.value === reasonValue);
  return opt?.label ?? reasonValue;
}

function UnsubscribeFeedbackFields({ submitting }: { submitting: boolean }) {
  const { watch } = useFormContext<{ reason: string; otherDetails: string }>();
  const reason = watch("reason");

  return (
    <>
      <RhfRadio
        label="Please tell us why you unsubscribed:"
        name="reason"
        // required
        options={UNSUBSCRIBE_REASONS}
        orientation="vertical"
        className="w-full"
        disabled={submitting}
      />
      {reason === "other" && (
        <RhfInput
          name="otherDetails"
          type="textarea"
          placeholder="Tell us more…"
          disabled={submitting}
        />
      )}
      <Button type="submit" className="mt-1" disabled={submitting}>
        {submitting ? "Submitting…" : "Submit"}
      </Button>
    </>
  );
}

function UnsubscribeFeedbackForm({ token }: { token: string }) {
  const [submitting, setSubmitting] = useState(false);

  return (
    <RHFWrapper
      initialValues={{ reason: "", otherDetails: "" }}
      isValidate
      validateSchema={unsubscribeFeedbackSchema}
      className="mt-10 space-y-4 w-full max-w-md"
      onSubmit={(data: { reason: string; otherDetails?: string }, methods) => {
        const trimmedToken = token.trim();
        if (!trimmedToken) {
          toast.error("Missing unsubscribe token.");
          return;
        }
        const reasonText = resolveFeedbackReasonText(
          data.reason,
          String(data.otherDetails ?? ""),
        );
        void (async () => {
          setSubmitting(true);
          try {
            const postUrl = new URL(
              `${apiBase}/public/notifications/email/unsubscribe`,
            );
            postUrl.searchParams.set("t", trimmedToken);

            const res = await fetch(postUrl.toString(), {
              method: "POST",
              credentials: "omit",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ t: trimmedToken, reason: reasonText }),
            });
            const text = await res.text();
            let body: unknown = null;
            try {
              body = text ? JSON.parse(text) : null;
            } catch {
              body = null;
            }
            if (isPublicApiSuccess(res.ok, body)) {
              toast.success(
                readPublicApiMessage(body) ?? "Thank you for your feedback.",
              );
              methods.reset({ reason: "", otherDetails: "" });
              setTimeout(() => {
                window.close();
              }, 1500);
            } else {
              toast.error(
                readPublicApiMessage(body) ??
                  "Could not submit feedback. Please try again.",
              );
            }
          } catch {
            toast.error("Something went wrong. Please try again.");
          } finally {
            setSubmitting(false);
          }
        })();
      }}
    >
      <UnsubscribeFeedbackFields submitting={submitting} />
    </RHFWrapper>
  );
}

function AnthroLogo() {
  const navigate = useNavigate();
  const [logoSrc, setLogoSrc] = useState("/logo.png");
  const didFallback = useRef(false);

  return (
    <div className="flex justify-center mb-10">
      <button
        type="button"
        className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        // onClick={() => navigate("/")}
        aria-label="Anthro Care home"
      >
        <img
          src={logoSrc}
          alt="Anthro Care"
          className="h-16 w-auto max-w-[200px] object-contain"
          onError={() => {
            if (!didFallback.current && logoSrc === "/logo.png") {
              didFallback.current = true;
              setLogoSrc("/logo.svg");
            }
          }}
        />
      </button>
    </div>
  );
}

export default function Unsubscribe() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token?.trim()) {
      setStatus("invalid");
      return;
    }

    let cancelled = false;

    async function run() {
      try {
        const url = new URL(`${apiBase}/public/notifications/email/unsubscribe`);
        url.searchParams.set("t", token || "");

        const res = await fetch(url.toString(), { method: "GET", credentials: "omit" });
        const text = await res.text();
        let body: unknown;
        try {
          body = text ? JSON.parse(text) : null;
        } catch {
          body = null;
        }

        if (cancelled) return;

        if (isPublicApiSuccess(res.ok, body)) {
          setStatus("success");
          setMessage(
            readPublicApiMessage(body) ??
              "You won't receive any more emails from Anthro Care.",
          );
          return;
        }

        setStatus("error");
        setMessage(
          readPublicApiMessage(body) ??
            "This link is invalid or has expired. If you still receive emails, contact support.",
        );
      } catch {
        if (!cancelled) {
          setStatus("error");
          setMessage("Something went wrong. Please try again later.");
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4 py-12">
      <div className="w-full max-w-md flex flex-col items-stretch">
        <AnthroLogo />

        {status === "loading" && (
          <div className="text-left space-y-2">
            <p className="text-lg text-muted-foreground">Processing your request…</p>
          </div>
        )}

        {status === "invalid" && (
          <div className="text-left space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Unsubscribe
            </h1>
            <p className="text-muted-foreground">
              This unsubscribe link is missing a valid token. Use the link from your email.
            </p>
          </div>
        )}

        {status === "success" && message && (
          <div className="text-left space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              You&apos;ve been unsubscribed.
            </h1>
            <p className="text-base text-muted-foreground">{message}</p>
            <UnsubscribeFeedbackForm token={token ?? ""} />
            <p className="text-sm text-center text-muted-foreground pt-2">
              Need Help?{" "}
              <a
                href="mailto:support@anthro.care?subject=Email%20subscription%20help"
                className="text-primary hover:underline"
              >
                support@anthro.care
              </a>
            </p>
          </div>
        )}

        {status === "error" && message && (
          <div className="text-left space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Something went wrong
            </h1>
            <p className="text-muted-foreground">{message}</p>
          </div>
        )}
      </div>
    </div>
  );
}
